import { memo, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { handleBuilderModeTab } from 'redux/BuilderModeSlice';
import {
  FetchBlockOutputs,
  getBaseScenario,
  getPlannerBlock,
  getPlannerModel,
  UpdateBlockDimension,
} from 'redux/PlannerModeSlice';
import { setActiveOverviewTab } from 'redux/ModelsSlice';
import { fetchBlock, FetchBlockItemDimensions } from 'redux/PlanPageSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../constants';
import BuilderModeSvgIcon from '../icons/BuilderModeSvgIcon';

import outlineActionButtonStyles from './outlineActionButtonStyles';

const BlockDimensionsDropdown = memo(() => {
  const { blockId, modelId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const flags = useFlags();
  const baseScenario = useSelector(getBaseScenario);
  const plannerModel = useSelector(getPlannerModel);
  const plannerBlock = useSelector(getPlannerBlock);

  const selectedCount = useMemo(
    () =>
      plannerBlock?.dimensions?.filter((item: { name: string }) => item.name !== 'Time').length ??
      0,
    [plannerBlock?.dimensions],
  );

  const doesDimensionExist = useCallback(
    (dim: { id: number }, dimArray: { id: number }[]) =>
      dimArray?.some((data) => data.id === dim.id),
    [],
  );

  const fetchIndicatorsData = useCallback(
    (dimensions: number[]) => {
      const idArray = [...dimensions];
      const timeDimension = plannerBlock?.dimensions.find(
        (object: { name: string }) => object.name === 'Time',
      );
      const timeIndex = idArray.indexOf(timeDimension?.id);
      if (timeIndex !== -1) {
        idArray.splice(timeIndex, 1);
      }
      dispatch(
        FetchBlockOutputs({
          blockId,
          params: {
            dim_id: idArray.join(', '),
            indicator_filter: 'all',
            scenario_id: baseScenario?.id,
          },
        }),
      );
    },
    [baseScenario?.id, blockId, dispatch, plannerBlock?.dimensions],
  );

  const handleSettingChange = useCallback(
    async (data: { id: number }, event: React.ChangeEvent<HTMLInputElement>) => {
      const dimensionsList = plannerBlock?.dimensions.filter(
        (item: { name: string }) => item.name !== 'Time',
      );
      let dimensions: { id: number }[] = plannerBlock?.dimensions || [];
      const dataIndex = dimensions.findIndex((item) => item.id === data.id);
      dimensions =
        dataIndex !== -1
          ? dimensions.filter((_, index) => index !== dataIndex)
          : [...dimensions, data];
      const dimensionIds = dimensions.map((item) => item.id);

      await dispatch(
        UpdateBlockDimension({
          blockId,
          request_body: { dimension_id: dimensionIds },
          toaster: true,
        }),
      ).then((response: any) => {
        if (!response?.error) {
          fetchIndicatorsData(dimensionIds);
          dispatch(fetchBlock(Number(blockId)));
        }
      });
      await dispatch(FetchBlockItemDimensions({ blockId }));

      if ((event.target.checked && dimensionsList?.length === 0) || !event.target.checked) {
        let dimension: { id: number }[] = plannerBlock?.dimensions || [];
        const dataIndexes = dimension.findIndex((item) => item.id === data.id);
        dimension =
          dataIndexes !== -1
            ? dimension.filter((_, index) => index !== dataIndexes)
            : [...dimension, data];
        const ids = dimension.map((item) => item.id);
        dispatch(UpdateBlockDimension({ blockId, request_body: { dimension_id: ids } })).then(
          (response: any) => {
            if (!response?.error) {
              fetchIndicatorsData(ids);
            }
          },
        );
      }
    },
    [blockId, dispatch, fetchIndicatorsData, plannerBlock?.dimensions],
  );

  const menuItems = useMemo(() => {
    const list = plannerModel?.dimensions ?? [];
    const timeObject = list.find((item: { name: string }) => item.name === 'Time');
    const remainingList = list.filter(
      (item: { id: number }) =>
        !plannerBlock?.dimensions.some((element: { id: number }) => element.id === item.id),
    );
    const dimensionsList = plannerBlock?.dimensions?.filter(
      (item: { name: string }) => item.name !== 'Time',
    );
    return [
      ...(timeObject ? [timeObject] : []),
      ...(dimensionsList || []),
      ...(remainingList || []),
    ];
  }, [plannerBlock?.dimensions, plannerModel?.dimensions]);

  const selectionLabel = selectedCount > 0 ? `(${selectedCount} selected)` : undefined;

  const navigateToDimensions = useCallback(() => {
    if (!modelId) return;

    if (flags?.uiRevamp) {
      dispatch(setActiveOverviewTab('dimensions'));
      navigate(`/model/${modelId}/dimensions`);
      return;
    }

    dispatch(handleBuilderModeTab(1));
    navigate(`/model/${modelId}/home`);
  }, [dispatch, flags?.uiRevamp, modelId, navigate]);

  return (
    <Menu closeOnSelect={false} isLazy>
      <MenuButton
        sx={outlineActionButtonStyles}
        aria-label='Block dimensions'
        h={BUILDER_MODE_V1_LAYOUT.actionButtonHeight}
      >
        <Flex align='center' gap='8px'>
          <BuilderModeSvgIcon name='listTree' />
          <Text
            fontSize='14px'
            fontWeight='400'
            color={BUILDER_MODE_V1_TOKENS.textDark}
            whiteSpace='nowrap'
          >
            Block Dimensions
          </Text>
          {selectionLabel && (
            <>
              <Box w='1px' h='17px' bg={BUILDER_MODE_V1_TOKENS.border} flexShrink={0} />
              <Text
                fontSize='14px'
                fontWeight='600'
                color={BUILDER_MODE_V1_TOKENS.primary}
                whiteSpace='nowrap'
              >
                {selectionLabel}
              </Text>
            </>
          )}
          <BuilderModeSvgIcon name='chevronDown' />
        </Flex>
      </MenuButton>
      <MenuList
        w='230px'
        pb={0}
        px={4}
        maxH='250px'
        zIndex={20}
        bg={BUILDER_MODE_V1_TOKENS.white}
        borderColor={BUILDER_MODE_V1_TOKENS.border}
        boxShadow='0px 0px 8px rgba(0, 0, 0, 0.25)'
        borderRadius='14px'
        mt={1}
      >
        <Box maxH='190px' overflowY='auto' className='scroll'>
          {menuItems.map((data: { id: number; name: string }, index: number) => (
            <MenuItem
              key={`${data.id}-${index}`}
              padding='.5rem 0'
              as='li'
              bg={BUILDER_MODE_V1_TOKENS.white}
            >
              <Switch
                mr={2}
                disabled={data.name === 'time' || data.name === 'Time'}
                isChecked={doesDimensionExist(data, plannerBlock?.dimensions ?? [])}
                onChange={(event) => handleSettingChange(data, event)}
                colorScheme='purple'
              />
              {data.name}
            </MenuItem>
          ))}
        </Box>
        <Divider color={BUILDER_MODE_V1_TOKENS.border} mt={2} />
        <Button
          fontSize='xs'
          px={0}
          pt={3}
          pb={2}
          variant='ghost'
          color={BUILDER_MODE_V1_TOKENS.primary}
          onClick={navigateToDimensions}
        >
          Edit dimensions
        </Button>
      </MenuList>
    </Menu>
  );
});

BlockDimensionsDropdown.displayName = 'BlockDimensionsDropdown';

export default BlockDimensionsDropdown;
